import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {Subject, takeUntil} from "rxjs";
import {Store} from "@ngrx/store";
import {
  CreateDuplicateSurveyRequest,
  FetchManageSurveyListRequest
} from "../../core/questionnaires.actions";
import {FetchManageSurveyList} from "../../core/questionnaires.selectors";
import {FormControl, FormGroup} from '@angular/forms';
import {drillDownUrls, profile} from "../../../../shared/core/shared.selectors";
import {CommonService} from "../../../../shared/services/common.service";
import {Sort} from "@angular/material/sort";
import {MatTableDataSource} from '@angular/material/table';
import { sortOrderMap } from 'src/app/modules/shared/constants/common.constants';
import {SetDrillDownUrls} from "../../../../shared/core/shared.actions";

@Component({
  selector: 'app-manage-questionnaires-screen',
  templateUrl: './manage-questionnaires-screen.component.html',
  styleUrls: ['./manage-questionnaires-screen.component.css']
})
export class ManageQuestionnairesScreenComponent implements OnInit, OnDestroy {
  expandPanel: any = true;
  surveyDisplayColumn: any[] = [];
  currentPageNumber: number = 0;
  hasNext: boolean = false;
  hasPrevious: boolean = false
  isPaginationLoading: boolean = false
  updatedPageNumber: number = 0
  loading: boolean = false;
  filterForm: FormGroup = new FormGroup({
    statuses: new FormControl([]),
    searchText: new FormControl('')
  })
  statusOptions: { key: number, value: string }[] = [
    { key: 1, value: 'Draft' },
    { key: 2, value: 'Open' },
    { key: 3, value: 'Closed' }
  ];
  isSearchedUser: boolean = true;
  groupId: string = '';
  clientId: string = ''
  loggedInUserRole: number = null
  sortColumn: any;
  sortOrder: any;
  isFirstPage: boolean = true;
  dataSource: any = new MatTableDataSource();
  private roleMap = {
    1: 'stanford-admin',
    2: 'organization-admin',
    3: 'coach',
  };
  sortOrderMap = sortOrderMap
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private router: Router,
    private store: Store,
    private route: ActivatedRoute,
    private commonService: CommonService
  ) {
  }

  ngOnInit(): void {
    this.store.select(profile).pipe(takeUntil(this.onDestroy))
      .subscribe(profile => {
        this.loggedInUserRole = profile?.role
      })
    this.route.paramMap.pipe(takeUntil(this.onDestroy))
      .subscribe((params: any) => {
        if (params.params.groupId || params.params.clientId) {
          //fetch according to group
          this.surveyDisplayColumn = [
            'title',
            'createdAt',
            'status',
            'view',
          ]
          this.groupId = params.params.groupId
          this.clientId = params.params.clientId
          this.store.dispatch(FetchManageSurveyListRequest({
            payload: {
              pageNumber: 1,
              ...(this.groupId ? {groupId: this.groupId} : {}),
              ...(this.clientId ? {clientId: this.clientId} : {})
            }
          }));
        } else {
          this.surveyDisplayColumn = [
            'title',
            'createdAt',
            'status',
            'view',
            'actions'
          ]
          this.store.dispatch(FetchManageSurveyListRequest({payload: {pageNumber: 1}}));
        }
      })

    this.store.select(FetchManageSurveyList).pipe(takeUntil(this.onDestroy))
      .subscribe(survey => {
        this.loading = survey.loading;
        this.hasNext = survey.details.hasNext;
        this.hasPrevious = survey.details.hasPrev;
        this.currentPageNumber = survey.details.pageNumber
        if (survey?.details?.list?.length > 0) {
          this.dataSource.data = survey.details.list
        } else {
          this.dataSource.data = []
        }
        if (this.hasNext && !this.loading) {
          setTimeout(() => {
            // check window height for infinite scroll
            this.commonService.checkInfiniteScrollHeightAndCallNext('table-wrapper', () => {
              this.getNext();
            });

            if(this.sortColumn && this.currentPageNumber <= 2 && !this.loading){
              this.commonService.scrollColumnInView(this.sortColumn)
            }

          }, 0)
        }
      });

    this.commonService
      .onNavigationEnd
      .pipe(takeUntil(this.onDestroy))
      .subscribe(d => {
        const urlParams = {
          params: this.route.snapshot.params,
          queryParams: this.route.snapshot.queryParams
        }
        const prevUrlParams = this.commonService.urlParams.getValue();

        if (d.isRouteChanged || JSON.stringify(prevUrlParams) !== JSON.stringify(urlParams)) {
         this.commonService.urlParams.next(urlParams);
        }
      });
  }


  navigateToCreateSurvey() {
    const urlPrefix = this.router.url.split('/')[1];

    this.router.navigate([`/${urlPrefix}/questionnaires/create-questionnaire-schedule`]);
  }

  onClickViewSurveyDetails(survey) {
    if (survey.status.key === 1) {
      this.router.navigate([`/stanford-admin/questionnaires/edit/create-questionnaire-schedule/${survey.id}`])
    } else {
      //need to fix here
      if (this.loggedInUserRole === 1 && !this.clientId && !this.groupId) {
        this.router.navigate([`/stanford-admin/questionnaires/edit-questionnaire/${survey.id}`])
      } else if (this.groupId || this.clientId) {
        if (this.groupId) {
          this.router.navigate([`/${this.roleMap[this.loggedInUserRole]}/group/${this.groupId}/questionnaires/edit-questionnaire/${survey.id}`])
        } else if (this.clientId) {
          this.router.navigate([`/${this.roleMap[this.loggedInUserRole]}/organization/${this.clientId}/questionnaires/edit-questionnaire/${survey.id}`])
        }
      }
    }
  }

  duplicateSurvey(survey) {
    this.store.dispatch(CreateDuplicateSurveyRequest({payload: {surveyId: survey.id}}))
  }

  getNext() {
    this.isFirstPage = false;
    let sortOrder = this.getSortOrder();
    if (this.hasNext && !this.loading) {
      this.currentPageNumber = this.currentPageNumber + 1
      this.isPaginationLoading = true;
      this.store.dispatch(FetchManageSurveyListRequest({
        payload: {
          pageNumber: this.currentPageNumber,
          ...(this.filterForm.value.statuses?.length ? {statuses: this.filterForm.value.statuses} : {}),
          ...(this.filterForm.value.searchText ? {searchText: this.filterForm.value.searchText} : {}),
          ...(this.groupId ? {groupId: this.groupId} : {}),
          ...(this.clientId ? {clientId: this.clientId} : {}),
          ...(this.sortColumn && sortOrder ? {sortColumn: this.sortColumn} : {}),
          ...(this.sortColumn && sortOrder ? {sortOrder: sortOrder} : {})
        }
      }));
    }
  }

  applyButtonClicked() {
    let sortOrder = this.getSortOrder();
    this.isSearchedUser = true;
    this.store.dispatch(FetchManageSurveyListRequest({
      payload: {
        pageNumber: 1,
        ...(this.filterForm.value.statuses?.length ? {statuses: this.filterForm.value.statuses} : {}),
        ...(this.filterForm.value.searchText ? {searchText: this.filterForm.value.searchText} : {}),
        ...(this.groupId ? {groupId: this.groupId} : {}),
        ...(this.clientId ? {clientId: this.clientId} : {}),
        ...(this.sortColumn && sortOrder ? {sortColumn: this.sortColumn} : {}),
        ...(this.sortColumn && sortOrder ? {sortOrder: sortOrder} : {})
      }
    }));
  }

  sortData(sort: Sort) {
    this.isFirstPage = true;
    this.sortColumn = sort.active;
    this.sortOrder = sort.direction
    let sortOrder = this.getSortOrder();
    this.store.dispatch(FetchManageSurveyListRequest({
      payload: {
        pageNumber: 1,
        ...(this.filterForm.value.statuses?.length ? {statuses: this.filterForm.value.statuses} : {}),
        ...(this.filterForm.value.searchText ? {searchText: this.filterForm.value.searchText} : {}),
        ...(this.groupId ? {groupId: this.groupId} : {}),
        ...(this.clientId ? {clientId: this.clientId} : {}),
        ...(this.sortColumn && sortOrder ? {sortColumn: this.sortColumn} : {}),
        ...(this.sortColumn && sortOrder ? {sortOrder: sortOrder} : {})
      }
    }));
  }

  getSortOrder(): number {
    return this.sortOrderMap[this.sortOrder]
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
